/**
 * Helper para preparar los datos de door programming antes de enviar a la API
 * Maneja la lógica de subscriptions_status y limpieza de fechas según el modo y checkboxes
 */

import { setToZeroTimeZone } from './dates-hanlder';

export interface SubscriptionsData {
  doorMode?: string;
  subscriptions_opening_date?: string | null;
  subscriptions_closing_date?: string | null;
  subscriptions_status?: 'open' | 'closed';
}

export interface PreparedSubscriptionsData {
  subscriptions_status: 'open' | 'closed';
  subscriptions_opening_date?: string | null;
  subscriptions_closing_date?: string | null;
}

/**
 * Prepara los datos de suscripciones para enviar a la API
 * Establece subscriptions_status y limpia fechas según el modo seleccionado por el usuario
 * Convierte fechas a UTC (timezone cero) antes de enviar
 * @param data - Datos del formulario con doorMode y fechas
 * @returns Datos preparados con subscriptions_status y fechas correctas en UTC
 */
export const prepareSubscriptionsData = (data: SubscriptionsData): PreparedSubscriptionsData => {
  const {
    doorMode,
    subscriptions_opening_date,
    subscriptions_closing_date
  } = data;

  // Objeto base
  const preparedData: PreparedSubscriptionsData = {
    subscriptions_status: 'open',
    subscriptions_opening_date: null,
    subscriptions_closing_date: null
  };

  switch (doorMode) {
    // CREACIÓN o EDICIÓN: Programar apertura de inscripciones
    case 'open_subscriptions':
      if (!subscriptions_opening_date) {
        // Checkbox "Abrir ahora" marcado → Abrir inmediatamente
        preparedData.subscriptions_status = 'open';
        preparedData.subscriptions_opening_date = null;
        // Mantener fecha de cierre si existe (convertir a UTC)
        preparedData.subscriptions_closing_date = subscriptions_closing_date
          ? setToZeroTimeZone(subscriptions_closing_date)
          : null;
      } else {
        // Hay fecha de apertura → Inscripciones cerradas hasta esa fecha (convertir ambas a UTC)
        preparedData.subscriptions_status = 'closed';
        preparedData.subscriptions_opening_date = setToZeroTimeZone(subscriptions_opening_date);
        preparedData.subscriptions_closing_date = subscriptions_closing_date
          ? setToZeroTimeZone(subscriptions_closing_date)
          : null;
      }
      break;

    // EDICIÓN - CERRADAS o CREACIÓN: Mantener cerradas
    case 'close_doors':
      preparedData.subscriptions_status = 'closed';
      preparedData.subscriptions_opening_date = null;
      preparedData.subscriptions_closing_date = null;
      break;

    // EDICIÓN - ABIERTAS: Mantener abiertas
    case 'open_doors':
      preparedData.subscriptions_status = 'open';
      preparedData.subscriptions_opening_date = null;
      preparedData.subscriptions_closing_date = null;
      break;

    // EDICIÓN - ABIERTAS: Cerrar inscripciones
    case 'close_subscriptions':
      if (!subscriptions_closing_date) {
        // Checkbox "Cerrar ahora" marcado → Cerrar inmediatamente
        preparedData.subscriptions_status = 'closed';
        preparedData.subscriptions_closing_date = null;
      } else {
        // Hay fecha de cierre programada → Permanece abierto hasta la fecha (convertir a UTC)
        preparedData.subscriptions_status = 'open';
        preparedData.subscriptions_closing_date = setToZeroTimeZone(subscriptions_closing_date);
      }
      preparedData.subscriptions_opening_date = null;
      break;

    default:
      // Por defecto, inscripciones abiertas sin programación
      preparedData.subscriptions_status = 'open';
      preparedData.subscriptions_opening_date = null;
      preparedData.subscriptions_closing_date = null;
  }

  return preparedData;
};

/**
 * Limpia los datos preparados eliminando campos null para no enviarlos a la API
 * @param data - Datos preparados
 * @returns Datos sin campos null
 */
export const cleanSubscriptionsData = (data: PreparedSubscriptionsData) => {
  const cleaned: any = {
    ...data
  };

  // Eliminar campos null o undefined
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === null || cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });

  return cleaned;
};

